import React, { useMemo, useState } from 'react';
import { Box, Button, Chip, Grid, Paper, Stack, Tab, Tabs, Typography } from '@mui/material';
import { AddRounded, ChairAltRounded, LocalDiningRounded, TakeoutDiningRounded } from '@mui/icons-material';
import { formatMoney, minutesSince } from '../utils/format';

function statusInfo(order) {
  if (!order) return { label: 'Libre', color: '#64748b', bg: '#f1f5f9', border: '#d8e0e8' };
  if (order.status === 'sent') return { label: 'En cocina', color: '#b45309', bg: '#fff7ed', border: '#fed7aa' };
  if (order.status === 'preparing') return { label: 'Preparando', color: '#b45309', bg: '#fff7ed', border: '#fdba74' };
  if (order.status === 'ready') return { label: 'Listo', color: '#15803d', bg: '#f0fdf4', border: '#bbf7d0' };
  if (order.status === 'served') return { label: 'Por cobrar', color: '#6d28d9', bg: '#f5f3ff', border: '#ddd6fe' };
  return { label: 'Abierta', color: '#1769c9', bg: '#eff6ff', border: '#bfdbfe' };
}

export default function SalonView({ tables, activeOrders, orderTotal, onOpenTable, onTakeaway, title = 'Salón', subtitle = 'Seleccione una mesa o cree un pedido para llevar.', showTakeaway = true }) {
  const zones = useMemo(() => Array.from(new Set(tables.map((table) => table.zona))), [tables]);
  const [zone, setZone] = useState(zones[0] || 'Salón');
  const visible = tables.filter((table) => table.zona === zone);
  const takeaway = activeOrders.filter((order) => order.type === 'takeaway');

  return (
    <Box sx={{ p: { xs: 1.2, md: 2 }, pt: 0 }}>
      <Paper elevation={0} sx={{ p: 1.5, border: '1px solid #d7e1ec', mb: 1.4 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={1}>
          <Box>
            <Typography sx={{ fontSize: 25, fontWeight: 950, color: '#17385f' }}>{title}</Typography>
            <Typography variant="body2" color="text.secondary">{subtitle}</Typography>
          </Box>
          {showTakeaway && onTakeaway ? <Button variant="contained" color="secondary" startIcon={<TakeoutDiningRounded />} onClick={onTakeaway}>Nuevo para llevar</Button> : null}
        </Stack>
      </Paper>

      <Paper elevation={0} sx={{ border: '1px solid #d7e1ec', overflow: 'hidden' }}>
        <Tabs value={zone} onChange={(_, value) => setZone(value)} sx={{ px: 1, borderBottom: '1px solid #e2e8f0' }}>
          {zones.map((item) => <Tab key={item} value={item} label={item} />)}
        </Tabs>
        <Box sx={{ p: 1.4 }}>
          <Grid container spacing={1.2}>
            {visible.map((table) => {
              const order = activeOrders.find((row) => row.tableId === table.id);
              const info = statusInfo(order);
              return (
                <Grid item xs={6} sm={4} md={3} xl={2} key={table.id}>
                  <Paper
                    elevation={0}
                    onClick={() => onOpenTable(table)}
                    sx={{ p: 1.5, minHeight: 150, cursor: 'pointer', border: `1px solid ${info.border}`, bgcolor: info.bg, transition: '.15s', '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 7px 18px rgba(35,65,95,.08)' } }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                      <Box sx={{ width: 42, height: 42, display: 'grid', placeItems: 'center', borderRadius: 1.5, bgcolor: '#fff', border: '1px solid rgba(0,0,0,.06)' }}>
                        <ChairAltRounded sx={{ color: info.color }} />
                      </Box>
                      <Chip size="small" label={info.label} sx={{ color: info.color, bgcolor: '#fff', fontWeight: 900 }} />
                    </Stack>
                    <Typography sx={{ mt: 1.2, fontWeight: 950, fontSize: 18 }}>{table.nombre}</Typography>
                    <Typography variant="caption" color="text.secondary">{table.capacidad} personas</Typography>
                    {order ? (
                      <Stack direction="row" justifyContent="space-between" sx={{ mt: 1 }}>
                        <Typography variant="body2" fontWeight={900}>{formatMoney(orderTotal(order))}</Typography>
                        <Typography variant="caption" color="text.secondary">{minutesSince(order.createdAt)} min</Typography>
                      </Stack>
                    ) : (
                      <Typography variant="body2" sx={{ mt: 1, color: '#64748b' }}>Disponible</Typography>
                    )}
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      </Paper>

      {takeaway.length ? (
        <Paper elevation={0} sx={{ mt: 1.4, p: 1.4, border: '1px solid #d7e1ec' }}>
          <Stack direction="row" alignItems="center" spacing={0.8} sx={{ mb: 1 }}>
            <LocalDiningRounded color="secondary" />
            <Typography fontWeight={950}>Pedidos para llevar</Typography>
          </Stack>
          <Stack direction="row" spacing={1} sx={{ overflowX: 'auto', pb: 0.5 }}>
            {takeaway.map((order) => (
              <Button key={order.id} variant="outlined" onClick={() => onOpenTable({ id: order.tableId, existingOrderId: order.id })} startIcon={<AddRounded />} sx={{ flex: '0 0 auto' }}>
                {order.label} · {formatMoney(orderTotal(order))}
              </Button>
            ))}
          </Stack>
        </Paper>
      ) : null}
    </Box>
  );
}
